/* Career map · pieces shared by the explorer views and the editor:
 * stage tones, icons, and how a cell's text is laid out. */
import React from 'react';
import { Briefcase, UserRound, ChevronDown } from 'lucide-react';
import { LT } from '../../contexts/AppCatalogueContext';
import { STAGE_TONES, splitTracks, toneFor } from './model';

/* ── Text of one cell: bullets, sub-headings, paragraphs ──────────── */

export function CellText({ text, compact }) {
  const split = splitTracks(text);
  if (split) {
    return (
      <div className="space-y-3">
        {split.intro.trim() && <Plain text={split.intro} compact={compact} />}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {split.tracks.map((t, i) => {
            const Icon = /manager/i.test(t.title) ? UserRound : Briefcase;
            return (
              <div key={i} className="rounded-xl p-3 min-w-0" style={{ background: '#fff', border: `1px solid ${LT.green30}` }}>
                <div className="flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.1em] font-medium mb-1.5" style={{ color: LT.greenDeep }}>
                  <Icon className="h-3.5 w-3.5" /> {t.title}
                </div>
                <Plain text={t.body} compact />
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return <Plain text={text} compact={compact} />;
}

export function Plain({ text, compact }) {
  const blocks = [];
  let list = null;
  (text || '').split('\n').forEach(raw => {
    const line = raw.trim();
    if (!line) { list = null; return; }
    if (line.startsWith('•')) {
      if (!list) { list = []; blocks.push({ list }); }
      list.push(line.replace(/^•\s*/, ''));
      return;
    }
    list = null;
    const heading = /^━+/.test(line) || /:\s*$/.test(line) || line === 'Tronc commun';
    blocks.push({ text: line.replace(/━+/g, '').trim(), heading });
  });
  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      {blocks.map((b, i) => b.list ? (
        <ul key={i} className="space-y-1 pl-4 list-disc marker:text-[#95A09B]">
          {b.list.map((li, j) => <li key={j}>{li}</li>)}
        </ul>
      ) : (
        <p key={i} className={b.heading ? 'text-[11.5px] uppercase tracking-[0.1em] font-medium pt-1' : ''}
           style={b.heading ? { color: LT.green50 } : undefined}>{b.text}</p>
      ))}
    </div>
  );
}

/* A level's keywords ("✦ Transmission · Référence / Faire grandir…") as a
 * headline: the first line big, the explanation under it. */
export function Tagline({ text }) {
  const lines = (text || '').split('\n').map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;
  return (
    <div className="mt-3 max-w-[640px]">
      <div className="font-display italic text-[1.25rem] leading-snug" style={{ color: LT.greenDeep }}>{lines[0].replace(/^✦\s*/, '')}</div>
      {lines.slice(1).map((l, i) => <div key={i} className="text-[14px]" style={{ color: '#4d6157' }}>{l}</div>)}
    </div>
  );
}

/* A key competency · its definition, then the anchors for a 3 ("conforme")
 * and a 5 ("fait référence") as two labelled lines. */
export function Competency({ comp, Icon }) {
  const lines = (comp.text || '').split('\n').map(l => l.trim()).filter(Boolean);
  const anchor = (n) => lines.find(l => l.startsWith(`${n} :`) || l.startsWith(`${n}:`));
  const rest = lines.filter(l => !/^[35]\s?:/.test(l));
  return (
    <details className="group rounded-xl px-3 py-2.5" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
      <summary className="cursor-pointer list-none flex items-center gap-2.5 text-[14px] font-medium" style={{ color: LT.greenDeep }}>
        <span className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ background: LT.greenSoft }}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="flex-1 min-w-0">{comp.name}</span>
        <ChevronDown className="h-4 w-4 transition group-open:rotate-180" style={{ color: LT.green50 }} />
      </summary>
      <div className="text-[13px] mt-2.5 leading-relaxed space-y-2" style={{ color: '#3a4f44' }}>
        {rest.map((l, i) => <p key={i}>{l}</p>)}
        {[3, 5].map(n => anchor(n) && (
          <div key={n} className="flex gap-2">
            <span className="h-5 w-5 shrink-0 rounded-full grid place-items-center font-num text-[11px]"
                  style={{ background: n === 3 ? '#D9DED9' : STAGE_TONES[3].fill, color: n === 3 ? LT.greenDeep : '#fff' }}>{n}</span>
            <span>{anchor(n).replace(/^[35]\s?:\s*/, '')}</span>
          </div>
        ))}
      </div>
    </details>
  );
}


/* The compact staircase · nine steps tinted by stage, always on screen
 * while reading a level. Fits a phone (nine small steps), grows on desktop. */
export function MiniLadder({ map, value, onPick, levelWord, sticky = true }) {
  const count = map.levels.length;
  return (
    <div className={`${sticky ? 'sticky top-[72px] z-20' : ''} -mx-2 px-2 py-2`} style={{ background: LT.beigeLight }}>
      <div className="grid gap-1 items-end" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
        {map.levels.map((l, i) => {
          const on = l.n === value;
          const tone = toneFor(map, l.n);
          return (
            <button key={l.n} type="button" onClick={() => onPick(l.n)} aria-pressed={on}
                    aria-label={`${levelWord} ${l.n} · ${l.name}`} title={`${l.n} · ${l.name}`}
                    className="relative text-left px-1.5 sm:px-2.5 pb-1.5 pt-1 flex flex-col justify-end transition-transform duration-200"
                    style={{
                      height: `${40 + i * 5}px`,
                      background: tone.fill, color: tone.ink,
                      boxShadow: on ? `0 0 0 2px ${LT.yellow}` : 'none',
                      transform: on ? 'translateY(-3px)' : 'none',
                    }}>
              <span className="font-num text-[13px] sm:text-[15px] leading-none">{l.n}</span>
              <span className="hidden lg:block text-[11px] leading-tight truncate mt-0.5 opacity-90">{l.name}</span>
            </button>
          );
        })}
      </div>
      <div className="grid gap-1 mt-1" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
        {map.stages.map((s, si) => (
          <div key={si} className="px-1.5 py-0.5 text-[10px] sm:text-[10.5px] uppercase tracking-[0.12em] truncate"
               style={{ gridColumn: `${s.levels[0]} / span ${s.levels.length}`, background: toneFor(map, s.levels[0]).soft, color: LT.greenDeep }}>
            {s.key}<span className="hidden sm:inline normal-case tracking-normal" style={{ color: '#4d6157' }}> · {s.question}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
